import { isCustomer } from '@app/auth/auth.utils';
import { NotFound } from '@app/components/table/components/not-found/NotFound';
import { GET_SCANNED_QR_CODE_BY_ID_QUERY } from '@app/constants/query-api-configs';
import { Routes } from '@app/constants/routes';
import { QRCodeApi } from '@app/domain/qr-code/api/qr-code.api';
import { GS1Preview } from '@app/domain/qr-code/components/input/gs1/GS1Preview';
import { MultilinkPreview } from '@app/domain/qr-code/components/input/multilink/MultilinkPreview';
import { QRPdfViewer } from '@app/domain/qr-code/components/input/pdf/QRPdfViewer';
import { WIFIPreview } from '@app/domain/qr-code/components/input/wifi/WIFIPreview';
import { useScanQRCode } from '@app/domain/scan/api/scan.hooks.api';
import { PasswordDialog } from '@app/domain/scan/components/vcard/PasswordDialog';
import { VCardPreview } from '@app/domain/scan/components/vcard/VCardPreview';
import { IS_LOCALHOST, IS_NOT_PROD_ENV } from '@app/environment/typed-env';
import { useGoogleClickId } from '@app/gtag/useGoogleClickId';
import { useHandler } from '@app/hooks/useHandler.hook';
import { useIsMobile } from '@app/hooks/useIsMobile.hook';
import { AUTH_QUERY_PARAM_AUTO_LINK_QR_ID } from '@app/modules/auth/auth.constants';
import { useTypedNavigate, useTypedParams } from '@app/router';
import { useStore } from '@app/store/useStore.hook';
import { clsxm } from '@app/styles/clsxm';
import { ECodeType, EScanResponseStatus } from '@app/swagger-types';
import { validateURL } from '@app/utils/url.util';
import { CircularProgress, SwipeableDrawer } from '@mui/material';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const ScanQRCodePage: React.FC = () => {
  const { id } = useTypedParams(Routes.qr_code.scanned);
  const [password, setPassword] = useState<string>('');
  const [submittedPassword, setSubmittedPassword] = useState<string | undefined>(undefined);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const { data, isLoading, refetch } = useScanQRCode(id, submittedPassword);
  const qr = data?.qr;
  const scan = data?.scan;
  const scanResponse = data?.scanResponse;

  const user = useStore.useCurrentUser();
  const { googleClickId } = useGoogleClickId();

  const isSuccessful = scanResponse?.status === EScanResponseStatus.SUCCESS;
  const isPasswordRequired = scanResponse?.passwordRequired;

  const [isShowPasswordDialog, setIsShowPasswordDialog] = useState(false);

  useEffect(() => {
    if (isPasswordRequired) {
      setIsShowPasswordDialog(true);
    }
  }, [isPasswordRequired]);

  useEffect(() => {
    if (scanResponse?.passwordIncorrect) {
      setPasswordError('Password is incorrect');
    }
    if (isSuccessful) {
      setIsOpenDrawer(false);
    }
  }, [scanResponse, isSuccessful]);

  const [isOpenDrawer, setIsOpenDrawer] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setIsOpenDrawer(true), 1000);
    return () => clearTimeout(timeout);
  }, []);

  const handleSubmit = useHandler(() => {
    if (password !== submittedPassword) {
      setSubmittedPassword(password);
      refetch({ queryKey: [GET_SCANNED_QR_CODE_BY_ID_QUERY.name, id, password] });
    }
  });

  const navigate = useTypedNavigate();

  const [memoShouldClaimOrExpired, reasonForDebug] = useMemo(() => {
    if (!qr) {
      return [false, 'no_qr'];
    }
    if (qr.hasOwnerDisputedPayment) {
      return [true, 'disputed_payment'];
    }
    try {
      const now = Date.now();
      if (qr.expireDate) {
        const expireDate = new Date(qr.expireDate);
        const isExpired = now > expireDate.getTime();
        if (isExpired) {
          return [true, 'expired'];
        }
        return [false, 'not_expired'];
      }
      const startDate = new Date(qr.createdDate);
      // should claim QR code
      if (now - DELTA_24_HOURS > startDate.getTime()) {
        return [true, 'should_claim'];
      }
    } catch (err) {
      console.error('error', err);
    }
    return [false, 'finally'];
  }, [qr]);

  useEffect(() => {
    if (!qr) {
      return;
    }
    if (!memoShouldClaimOrExpired) {
      return;
    }
    let mounted = true;
    // only linked to customers QR codes have expiration date
    if (qr.expireDate) {
      navigate({ to: Routes.authSpecial.expired_qr, queryParams: { [AUTH_QUERY_PARAM_AUTO_LINK_QR_ID]: qr.id } });
      return;
    }
    if (!user || !isCustomer(user)) {
      navigate({ to: Routes.authSpecial.claim_qr, queryParams: { [AUTH_QUERY_PARAM_AUTO_LINK_QR_ID]: qr.id } });
      return;
    }
    QRCodeApi.claim({ qrId: qr.id, googleClickId }).then(() => {
      if (!mounted) {
        return;
      }
      navigate({ to: Routes.customer.my_qr_codes.index });
    });
    return () => {
      mounted = false;
    };
  }, [qr, navigate, memoShouldClaimOrExpired, user, googleClickId]);

  if (IS_LOCALHOST) {
    console.log('qr reason', reasonForDebug, user);
  }

  const showLoader = isLoading || !scan || !qr || memoShouldClaimOrExpired;
  const isDisplayPreview = scan && ((isSuccessful && isPasswordRequired) || !isPasswordRequired);

  useEffect(() => {
    if (!qr) {
      return;
    }
    if (memoShouldClaimOrExpired) {
      return;
    }
    const urlForRedirect = qr.codeType === ECodeType.WEBSITE && qr.website?.url;
    if (!urlForRedirect || typeof urlForRedirect !== 'string') {
      return;
    }
    const normalURL = validateURL(urlForRedirect).url ? urlForRedirect : `https://${urlForRedirect}`;
    window.location.href = normalURL;
  }, [qr, memoShouldClaimOrExpired]);

  const QRPreview = useMemo(() => {
    if (scan?.codeType === ECodeType.V_CARD && scan.vcard) {
      return <VCardPreview vcard={scan.vcard} />;
    }
    if (scan?.codeType === ECodeType.MULTILINK && scan.multilink) {
      return <MultilinkPreview multilink={scan.multilink} />;
    }
    if (scan?.codeType === ECodeType.PDF && scan.pdf) {
      return <QRPdfViewer pdf={scan.pdf} />;
    }
    if (scan?.codeType === ECodeType.GS1 && scan.gs1) {
      return <GS1Preview gs1={scan.gs1} />;
    }
    if (scan?.codeType === ECodeType.WIFI && scan.wifi?.ssid && scan.wifi?.encryption) {
      return <WIFIPreview wifi={scan.wifi} />;
    }
    return null;
  }, [scan]);

  const shouldShowPasswordPrompt = !isDisplayPreview && isPasswordRequired;
  const shouldShowPreview = isDisplayPreview && isPasswordRequired;
  const shouldShowLoader = showLoader && !isPasswordRequired;
  const shouldShowNotFound = !isLoading && !qr;
  const shouldRenderQRContent = shouldShowPreview || !isPasswordRequired;

  return (
    <div
      className={clsxm('h-full w-full', showLoader && 'flex items-center justify-center')}
      aria-hidden="true"
      onClick={() => {
        if (shouldShowPasswordPrompt && !isOpenDrawer) {
          setIsOpenDrawer(true);
        }
      }}
    >
      <PasswordComponent
        password={password}
        setPassword={setPassword}
        passwordError={passwordError}
        handleSubmit={handleSubmit}
        setIsOpenDrawer={setIsOpenDrawer}
        isOpenDrawer={isOpenDrawer}
        withWrapper={isShowPasswordDialog}
      >
        {shouldShowPasswordPrompt && <p>Please enter the password</p>}
        {shouldShowLoader && <CircularProgress />}

        {shouldRenderQRContent && <>{QRPreview}</>}

        {shouldShowNotFound && <NotFound />}
      </PasswordComponent>
    </div>
  );
};

export const PasswordComponent: React.FC<{
  children?: ReactNode;
  password: string;
  setPassword: (password: string) => void;
  passwordError: string | null;
  handleSubmit: () => void;
  isOpenDrawer: boolean;
  setIsOpenDrawer: (state: boolean) => void;
  withWrapper: boolean | null;
}> = ({
  children,
  isOpenDrawer,
  setIsOpenDrawer,
  password,
  withWrapper = true,
  setPassword,
  passwordError,
  handleSubmit,
}) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const onBack = () => {
    navigate(-1);
  };

  if (!withWrapper) {
    return <>{children}</>;
  }

  return (
    <>
      {!isMobile && (
        <>
          {children}
          <div
            className={clsxm(
              `fixed inset-0 flex justify-center items-center transition-all duration-500 `,
              isOpenDrawer && 'bg-black/50',
              !isOpenDrawer && 'pointer-events-none'
            )}
            aria-hidden="true"
            onClick={() => setIsOpenDrawer(false)}
          >
            <div
              aria-hidden="true"
              className={clsxm('translate-y-[200%] duration-500 transition-all', isOpenDrawer && 'translate-y-0')}
              onClick={(e) => e.preventDefault()}
            >
              <PasswordDialog
                onBack={onBack}
                password={password}
                setPassword={setPassword}
                onSubmit={handleSubmit}
                error={passwordError}
              />
            </div>
          </div>
        </>
      )}
      {isMobile && (
        <>
          {children}
          <SwipeableDrawer
            anchor={'bottom'}
            open={isOpenDrawer}
            onOpen={() => setIsOpenDrawer(true)}
            onClose={() => setIsOpenDrawer(false)}
            swipeAreaWidth={120}
            className={!isOpenDrawer ? 'pointer-events-none' : ''}
            ModalProps={{ keepMounted: true }}
            classes={{
              paper: /*tw*/ 'bg-transparent rounded-tr-3xl rounded-tl-3xl',
            }}
          >
            <PasswordDialog
              onBack={onBack}
              password={password}
              setPassword={setPassword}
              onSubmit={handleSubmit}
              error={passwordError}
            />
          </SwipeableDrawer>
        </>
      )}
    </>
  );
};

const DELTA_24_HOURS = IS_NOT_PROD_ENV
  ? // 5 min for localhost
    1e3 * 60 * 5
  : 1e3 * 60 * 60 * 24;
